import {
  hashKey,
  queryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect } from 'react'
import { getTransactionsAdmin } from '@/queries/transactions/queries.ts'
import { AdminTransactionsSchemaType } from '@/queries/transactions/transactions.types.ts'

type Identifier = { provider_id: number } | { applicant_id: number }

const ONE_MINUTE = 60 * 1000
const PROVIDER_TRANSACTIONS_PER_PAGE = 10
const PROVIDER_TRANSACTIONS_GLOBAL_ON_ID = 1
const PROVIDER_TRANSACTIONS_LOCALE = 'en'

export const GET_ADMIN_TRANSACTIONS_QUERY_KEY = (
  program_id: number,
  currentPage: number,
  recordsPerPage: number,
  search: string,
  global_on_id: number,
  locale: string,
  identifier: Identifier
) => [
  'adminTransactions',
  { program_id, global_on_id, locale, identifier },
  { currentPage, recordsPerPage, search },
]

export const adminTransactionsQueryOptions = (
  program_id: number,
  currentPage: number,
  recordsPerPage: number,
  search: string,
  global_on_id: number,
  locale: string,
  identifier: Identifier
) =>
  queryOptions<AdminTransactionsSchemaType>({
    queryKey: GET_ADMIN_TRANSACTIONS_QUERY_KEY(
      program_id,
      currentPage,
      recordsPerPage,
      search,
      global_on_id,
      locale,
      identifier
    ),
    queryFn: () =>
      getTransactionsAdmin(
        program_id,
        currentPage,
        recordsPerPage,
        search,
        global_on_id,
        locale,
        identifier
      ),
    enabled:
      !!program_id &&
      !!currentPage &&
      !!recordsPerPage &&
      !!global_on_id &&
      !!locale,
    staleTime: ONE_MINUTE,
  })

export const providerTransactionsQueryOptions = (
  program_id: number,
  provider_id: number,
  currentPage = 1,
  recordsPerPage = PROVIDER_TRANSACTIONS_PER_PAGE,
  search = ''
) =>
  adminTransactionsQueryOptions(
    program_id,
    currentPage,
    recordsPerPage,
    search,
    PROVIDER_TRANSACTIONS_GLOBAL_ON_ID,
    PROVIDER_TRANSACTIONS_LOCALE,
    { provider_id }
  )

export const useAdminTransactions = (
  program_id: number,
  currentPage: number,
  recordsPerPage: number,
  search: string,
  global_on_id: number,
  locale: string,
  identifier: Identifier
) => {
  const queryClient = useQueryClient()

  const options = adminTransactionsQueryOptions(
    program_id,
    currentPage,
    recordsPerPage,
    search,
    global_on_id,
    locale,
    identifier
  )

  const query = useQuery({
    ...options,
    placeholderData: (previousData, previousQuery) =>
      previousQuery &&
      hashKey([previousQuery.queryKey[1]]) === hashKey([options.queryKey[1]])
        ? previousData
        : undefined,
  })

  const nextPage = currentPage + 1
  const hasNextPage =
    !!query.data && !query.isPlaceholderData && nextPage <= query.data.last_page

  useEffect(() => {
    if (!hasNextPage) return

    queryClient.prefetchQuery(
      adminTransactionsQueryOptions(
        program_id,
        nextPage,
        recordsPerPage,
        search,
        global_on_id,
        locale,
        identifier
      )
    )
  }, [hasNextPage, hashKey(options.queryKey)])

  return query
}
